import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Tag,
} from "lucide-react";
import { POSTS, getPostBySlug, AFFILIATE_URL } from "@/blog/posts";
import { setPageSeo } from "@/blog/seo";

import { HowToRankBody } from "@/blog/content/HowToRankBody";
import { WhatIsAeoBody } from "@/blog/content/WhatIsAeoBody";
import { GeoVsSeoBody } from "@/blog/content/GeoVsSeoBody";
import { BestAiSeoToolsBody } from "@/blog/content/BestAiSeoToolsBody";
import { RankHigherChatgptGoogleBody } from "@/blog/content/RankHigherChatgptGoogleBody";
import { HowToGetCitedByChatgptBody } from "@/blog/content/HowToGetCitedByChatgptBody";
import { AiSeoForSoloFoundersBody } from "@/blog/content/AiSeoForSoloFoundersBody";
import { AiSeoForWordpressBody } from "@/blog/content/AiSeoForWordpressBody";
import { GrowBusinessNationallyBody } from "@/blog/content/GrowBusinessNationallyBody";
import Ad from "@/blog/Ad";

const SITE = "https://growyourpresence.biz";

const BODIES = {
  "how-to-rank-on-ai-search-engines": HowToRankBody,
  "what-is-aeo-answer-engine-optimization": WhatIsAeoBody,
  "geo-vs-seo": GeoVsSeoBody,
  "best-ai-seo-tools-2026": BestAiSeoToolsBody,
  "rank-higher-on-chatgpt-and-google": RankHigherChatgptGoogleBody,
  "how-to-get-cited-by-chatgpt": HowToGetCitedByChatgptBody,
  "ai-seo-software-for-solo-founders": AiSeoForSoloFoundersBody,
  "ai-seo-platform-for-wordpress": AiSeoForWordpressBody,
  "grow-your-business-online-nationally": GrowBusinessNationallyBody,
};

const trackBlogCta = (location, postSlug) => {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.gtag === "function") {
      window.gtag("event", "affiliate_cta_click", {
        cta_location: `blog-${location}`,
        cta_destination: AFFILIATE_URL,
        post_slug: postSlug,
      });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "affiliate_cta_click",
      cta_location: `blog-${location}`,
      cta_destination: AFFILIATE_URL,
      post_slug: postSlug,
    });
    if (window.posthog && typeof window.posthog.capture === "function") {
      window.posthog.capture("affiliate_cta_click", {
        location: `blog-${location}`,
        destination: AFFILIATE_URL,
        post_slug: postSlug,
      });
    }
  } catch {
    /* noop */
  }
};

const FooterCTA = ({ post }) => (
  <a
    data-testid="blog-cta-footer"
    href={AFFILIATE_URL}
    target="_blank"
    rel="sponsored noopener noreferrer"
    onClick={() => trackBlogCta("footer", post.slug)}
    className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#FF512F] to-[#F09819] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-white shadow-[0_10px_40px_-10px_rgba(255,81,47,0.7)] transition-all hover:-translate-y-0.5"
  >
    Start growing with Soro
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
  </a>
);

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!post) return;
    const url = `${SITE}/blog/${post.slug}`;
    setPageSeo({
      title: `${post.metaTitle} | Grow Your Presence Online Daily`,
      description: post.description,
      canonical: url,
      ogImage: post.cover,
      ogType: "article",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
          headline: post.title,
          description: post.description,
          image: [post.cover],
          datePublished: post.dateISO,
          dateModified: post.dateISO,
          author: {
            "@type": "Person",
            name: post.author.name,
            url: post.author.url,
          },
          publisher: { "@id": `${SITE}/#organization` },
          keywords: post.keywords.join(", "),
          inLanguage: "en",
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        post.howto && {
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: post.howto.name,
          step: post.howto.steps.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.name,
            text: s.text,
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        },
      ],
    });
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const Body = BODIES[post.slug];
  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <article className="relative pt-28 pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 -top-32 h-[420px] w-[420px] rounded-full bg-[#FF512F] opacity-25 blur-[140px]" />
        <div className="absolute right-[-10%] top-[20%] h-[360px] w-[360px] rounded-full bg-[#F09819] opacity-20 blur-[140px]" />
      </div>

      <div className="mx-auto max-w-3xl px-6">
        <Link
          to="/blog"
          data-testid="blog-back-link"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> All posts
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-8"
        >
          <div className="flex flex-wrap items-center gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#F09819]"
              >
                <Tag className="h-3 w-3" /> {t}
              </span>
            ))}
          </div>
          <h1
            data-testid="blog-post-title"
            className="mt-5 font-display text-4xl font-black leading-tight tracking-tight sm:text-5xl"
          >
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-xs text-white/55">
            <span className="inline-flex items-center gap-1.5">
              By <span className="font-semibold text-white/85">{post.author.name}</span>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(post.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" /> {post.readMinutes} min read
            </span>
          </div>
        </motion.header>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 overflow-hidden rounded-3xl border border-white/10"
        >
          <img
            src={post.cover}
            alt={post.coverAlt}
            className="aspect-[16/9] w-full object-cover"
          />
        </motion.div>

        <Ad slot="blog-post-top" />

        <div className="prose-blog mt-10 max-w-none">
          {Body ? (
            <Body post={post} affiliateUrl={AFFILIATE_URL} onTrack={trackBlogCta} />
          ) : (
            <p className="text-white/70">Content coming soon.</p>
          )}
        </div>

        <Ad slot="blog-post-bottom" />

        <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.025] p-7">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#F09819]">
            About the author
          </div>
          <div className="mt-3 flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-[#FF512F] to-[#F09819] font-display text-sm font-extrabold text-white">
              {post.author.name
                .split(" ")
                .map((p) => p[0])
                .join("")}
            </div>
            <div>
              <div className="font-display font-bold">{post.author.name}</div>
              <a
                href={post.author.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-white/55 hover:text-white"
              >
                Connect on LinkedIn →
              </a>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-16">
            <div className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white/45">
              Keep reading
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="glass group rounded-2xl p-5 transition-all hover:-translate-y-0.5"
                >
                  <div className="font-display text-base font-bold leading-snug group-hover:text-gradient-flame">
                    {r.title}
                  </div>
                  <div className="mt-2 text-xs text-white/55 line-clamp-2">{r.description}</div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 rounded-3xl border border-[#FF512F]/30 bg-gradient-to-r from-[#FF512F]/15 to-[#F09819]/10 p-8">
          <div className="font-display text-2xl font-extrabold">
            Make every blog post you ship AI-ready.
          </div>
          <div className="mt-2 text-sm text-white/70">
            Soro auto-injects FAQ + HowTo schema, writes in answer blocks, and publishes daily.
          </div>
          <div className="mt-6">
            <FooterCTA post={post} />
          </div>
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
